import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import datV2 from '../services/datV2';
import {
  exportReportPdf,
  exportReportXlsx,
  buildReportRows,
  deriveLicenseFromCourse,
  DEFAULT_SCHOOL,
  DEFAULT_SIGNER,
} from '../exports/sessionReport';

function deriveStudentFromSessions(sessions) {
  if (!sessions?.length) return null;
  const first = sessions[0];
  // Tương thích v1 (snake_case) + v2 (camelCase).
  return {
    fullName: first.traineeName ?? first.trainee_name ?? '',
    studentCode: first.traineeRegistrationCode ?? first.trainee_ma_dk ?? '',
    license: deriveLicenseFromCourse(first.courseShortName ?? first.ten_khoa_hoc),
  };
}

function pickCourseCode(record) {
  if (!record) return '';
  return (
    record['mã khóa'] ||
    record['mã khoá'] ||
    record['Mã khóa học'] ||
    record['Mã khoá học'] ||
    record['ma_khoa_hoc'] ||
    ''
  );
}

function uniqueStudentCodes(sessions) {
  return Array.from(new Set(
    (sessions || []).map((s) => s.traineeRegistrationCode ?? s.trainee_ma_dk).filter(Boolean),
  ));
}

export default function SessionReportExportModal({ show, onHide, sessions }) {
  const [format, setFormat] = useState('pdf');
  const [skipViolations, setSkipViolations] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetchingStudent, setFetchingStudent] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    studentCode: '',
    birthday: '',
    courseCode: '',
    license: '',
    school: DEFAULT_SCHOOL,
    signer: DEFAULT_SIGNER,
  });

  const studentCodes = useMemo(() => uniqueStudentCodes(sessions), [sessions]);
  const multipleStudents = studentCodes.length > 1;

  const previewRows = useMemo(
    () => buildReportRows(sessions, { skipViolations }),
    [sessions, skipViolations]
  );

  useEffect(() => {
    if (!show) return;
    const derived = deriveStudentFromSessions(sessions);
    if (!derived) return;
    setForm((prev) => ({
      ...prev,
      fullName: derived.fullName,
      studentCode: derived.studentCode,
      license: derived.license,
      courseCode: prev.courseCode,
      birthday: prev.birthday,
      school: prev.school || DEFAULT_SCHOOL,
      signer: prev.signer || DEFAULT_SIGNER,
    }));

    if (!derived.studentCode) return;
    setFetchingStudent(true);
    let normalized = derived.studentCode
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[đĐ]/g, (m) => (m === 'đ' ? 'd' : 'D'));
    datV2
      .searchStudent({ name: normalized })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const match = list.find(
          (r) => (r.registrationCode ?? r['Mã đăng ký']) === derived.studentCode
        ) || list[0];
        if (match) {
          setForm((prev) => ({
            ...prev,
            birthday: match.dob ?? match['Ngày sinh'] ?? prev.birthday,
            courseCode: match.courseCode ?? pickCourseCode(match) ?? prev.courseCode,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setFetchingStudent(false));
  }, [show, sessions]);

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleExport = async () => {
    if (!sessions?.length) {
      toast('Không có phiên nào để xuất', { type: 'warning' });
      return;
    }
    if (multipleStudents) {
      toast('Vui lòng lọc theo 1 mã học viên trước khi xuất báo cáo', { type: 'warning' });
      return;
    }
    if (!form.fullName || !form.studentCode) {
      toast('Vui lòng nhập họ tên và mã học viên', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      if (format === 'pdf') {
        await exportReportPdf(form, sessions, { skipViolations });
      } else {
        exportReportXlsx(form, sessions, { skipViolations });
      }
      toast('Xuất báo cáo thành công', { type: 'success' });
      onHide();
    } catch (err) {
      console.error(err);
      toast('Xuất báo cáo thất bại', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Xuất báo cáo quá trình đào tạo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {multipleStudents && (
          <Alert variant='warning'>
            Dữ liệu hiện chứa {studentCodes.length} mã học viên khác nhau. Vui lòng tra cứu theo 1 mã học viên trước khi xuất báo cáo.
          </Alert>
        )}
        {!sessions?.length && (
          <Alert variant='warning'>Không có phiên nào để xuất.</Alert>
        )}

        <h6 className='mt-2'>Thông tin học viên</h6>
        <Row className='g-2'>
          <Col md={6}>
            <Form.Label>Họ và tên</Form.Label>
            <Form.Control value={form.fullName} onChange={update('fullName')} />
          </Col>
          <Col md={6}>
            <Form.Label>Mã học viên</Form.Label>
            <Form.Control value={form.studentCode} onChange={update('studentCode')} />
          </Col>
          <Col md={6}>
            <Form.Label>
              Ngày sinh {fetchingStudent && <small className='text-muted'>(đang tải...)</small>}
            </Form.Label>
            <Form.Control
              value={form.birthday}
              onChange={update('birthday')}
              placeholder='dd/MM/yyyy'
            />
          </Col>
          <Col md={6}>
            <Form.Label>Mã khóa học</Form.Label>
            <Form.Control value={form.courseCode} onChange={update('courseCode')} />
          </Col>
          <Col md={6}>
            <Form.Label>Hạng đào tạo</Form.Label>
            <Form.Control value={form.license} onChange={update('license')} />
          </Col>
          <Col md={6}>
            <Form.Label>Cơ sở đào tạo</Form.Label>
            <Form.Control value={form.school} onChange={update('school')} />
          </Col>
          <Col md={6}>
            <Form.Label>Người ký (cơ sở đào tạo)</Form.Label>
            <Form.Control value={form.signer} onChange={update('signer')} />
          </Col>
        </Row>

        <hr />
        <h6>Tuỳ chọn xuất</h6>
        <Row className='g-2 align-items-center'>
          <Col md={6}>
            <Form.Label>Định dạng</Form.Label>
            <Form.Select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value='pdf'>PDF</option>
              <option value='xlsx'>Excel (XLSX)</option>
            </Form.Select>
          </Col>
          <Col md={6} className='pt-4'>
            <Form.Check
              type='checkbox'
              label='Bỏ qua các phiên có vi phạm'
              checked={skipViolations}
              onChange={(e) => setSkipViolations(e.target.checked)}
            />
          </Col>
        </Row>

        <div className='mt-3 p-2 border rounded bg-light'>
          <strong>Tổng kết:</strong> {previewRows.rows.length} phiên — Tổng TG{' '}
          {previewRows.totalDuration.toFixed(2)}h, Tổng QĐ {previewRows.totalDistance.toFixed(2)} km.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={loading}>
          Đóng
        </Button>
        <Button
          variant='primary'
          onClick={handleExport}
          disabled={loading || multipleStudents || !sessions?.length}
        >
          {loading ? 'Đang xuất...' : `Xuất ${format.toUpperCase()}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
